import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import StoryNavigationController from '../../components/ui/StoryNavigationController';
import TopStoryDots from '../../components/ui/TopStoryDots';
import BottomFloatingMenu from '../../components/ui/BottomFloatingMenu';
import useGuestParameters from '../../hooks/useGuestParameters';
import { useContent } from '../../contexts/ContentContext';
import { getTemplate } from '../../config/templates';
import StorySection from './components/StorySection';
import WelcomeOverlay from './components/WelcomeOverlay';
import LoadingSkeleton from './components/LoadingSkeleton';

const FloralTemplate = lazy(() => import('./floral/FloralTemplate'));
const SageTemplate = lazy(() => import('./sage/SageTemplate'));
const PinkTemplate = lazy(() => import('./pink/PinkTemplate'));
const PeachTemplate = lazy(() => import('./peach/PeachTemplate'));

const WeddingInvitationDisplay = () => {
  const guestData = useGuestParameters();
  const { guestId, guestName, coupleNames, numberOfGuests, isLoading: guestLoading } = guestData;
  const { content, loading: contentLoading } = useContent();
  const [currentSection, setCurrentSection] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const storySections = content?.storySections || [];
  const meta = content?.meta || {};
  const couple = content?.couple || {};
  const event = content?.event || {};

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitLoading(false);
    }, 1500);

    // Check if welcome was already shown
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome') === 'true';
    if (hasSeenWelcome) {
      setShowWelcome(false);
    }

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (guestId) {
      console.log('Guest tracking:', { guestId, guestName, timestamp: new Date()?.toISOString() });
    }
  }, [guestId, guestName]);

  const handleSectionChange = (newSection) => {
    setCurrentSection(newSection);
  };

  const handleDismissWelcome = () => {
    setShowWelcome(false);
    localStorage.setItem('hasSeenWelcome', 'true');
  };

  // Touch event handlers for swipe gestures
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentSection < storySections.length - 1) {
      handleSectionChange(currentSection + 1);
    }

    if (isRightSwipe && currentSection > 0) {
      handleSectionChange(currentSection - 1);
    }
  };

  if (guestLoading || contentLoading || isInitLoading) {
    return <LoadingSkeleton />;
  }

  // Check if this is a page-scroll template (floral, sage, etc.)
  const templateId = content?.selectedTemplate || 'timeless';
  const templateDef = getTemplate(templateId);

  if (templateDef?.layout === 'page-scroll') {
    const PAGE_SCROLL_TEMPLATES = {
      sage: SageTemplate,
      pink: PinkTemplate,
      peach: PeachTemplate,
    };
    const TemplateComponent = PAGE_SCROLL_TEMPLATES[templateId] || FloralTemplate;
    return (
      <Suspense fallback={<LoadingSkeleton />}>
        <TemplateComponent content={content} guestName={guestName} guestData={guestData} />
      </Suspense>
    );
  }

  // Default: horizontal story-scroll layout
  return (
    <>
      <Helmet>
        <title>{meta.pageTitle || "Wedding Invitation"}</title>
        <meta name="description" content={meta.description || "You're invited to celebrate our special day."} />
        <meta property="og:title" content={meta.ogTitle || "Wedding Invitation"} />
        <meta property="og:description" content={meta.ogDescription || "You're invited to celebrate our special day."} />
        <meta property="og:image" content={meta.ogImage || ""} />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className="relative w-full min-h-screen bg-background overflow-hidden">
        <div className="w-full h-screen overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out h-full"
            style={{ transform: `translateX(-${currentSection * 100}%)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}>

            {storySections?.map((section) =>
            <div key={section?.id} className="w-full h-full flex-shrink-0">
                <StorySection section={section} />
              </div>
            )}
          </div>
        </div>

        <TopStoryDots
          totalSections={storySections?.length}
          currentSection={currentSection}
          onSectionChange={handleSectionChange}
        />

        <StoryNavigationController
          totalSections={storySections?.length}
          currentSection={currentSection}
          onSectionChange={handleSectionChange} />


        <BottomFloatingMenu />

        {showWelcome &&
        <WelcomeOverlay
          guestName={guestName}
          coupleNames={coupleNames || couple.names}
          weddingDate={event.date}
          venue={event.venue}
          welcomeMessage={content?.welcome?.message}
          onDismiss={handleDismissWelcome} />

        }
      </div>
    </>);

};

export default WeddingInvitationDisplay;
